import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { ALERT_RED, DARK_SURFACES, OFF_WHITE } from "@/shared/theme";
import {
  authKnightTransform,
  authMutedTextSx,
  AUTH_KNIGHT_ANIMATION,
  AUTH_KNIGHT_FRAMES,
  AUTH_KNIGHT_PATH,
} from "./auth-surface";

interface AuthCardProps {
  children: ReactNode;
  eyebrow?: string;
  title: string;
  detail?: ReactNode;
}

const board = Array.from({ length: 16 }, (_, index) => index);

/** Square editorial frame shared by every guest authentication state. */
export const AuthCard = ({ children, eyebrow = "PRIVATE COACHING WORKSPACE", title, detail }: AuthCardProps) => (
  <Box
    component="main"
    sx={{
      bgcolor: DARK_SURFACES.canvas,
      color: OFF_WHITE.main,
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.1fr) minmax(420px, .9fr)" },
      height: "100dvh",
      minHeight: "100dvh",
      overflowY: "auto",
    }}
  >
    <Box
      component="section"
      aria-label="Chessla"
      sx={{
        borderBottom: { xs: `2px solid ${ALERT_RED.main}`, md: 0 },
        borderRight: { md: `2px solid ${ALERT_RED.main}` },
        display: "grid",
        minHeight: { xs: 230, md: "100dvh" },
        overflow: "hidden",
        p: { xs: "max(24px, env(safe-area-inset-top)) 20px 28px", sm: 5, lg: 7 },
        position: "relative",
      }}
    >
      <Box sx={{ alignSelf: "start", display: "flex", justifyContent: "space-between" }}>
        <Typography
          sx={{ color: ALERT_RED.main, fontFamily: "monospace", fontSize: 11, fontWeight: 900, letterSpacing: ".12em" }}
        >
          CHESSLA / ACCOUNT
        </Typography>
        <Typography sx={{ fontFamily: "monospace", fontSize: 10 }}>YOUR GAMES · YOUR EVIDENCE</Typography>
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          bottom: { xs: -48, md: 42 },
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          aspectRatio: "1 / 1",
          opacity: 0.24,
          position: "absolute",
          right: { xs: -28, md: 48 },
          transform: "rotate(-7deg)",
          width: { xs: 190, md: 300 },
        }}
      >
        {board.map((square) => (
          <Box
            key={square}
            sx={{
              bgcolor: (Math.floor(square / 4) + square) % 2 ? OFF_WHITE.main : ALERT_RED.main,
              border: `1px solid ${DARK_SURFACES.canvas}`,
            }}
          />
        ))}
        <Box
          component="span"
          data-auth-nikola="true"
          sx={{
            position: "absolute",
            inset: 0,
            width: "25%",
            aspectRatio: "1 / 1",
            display: "grid",
            placeItems: "center",
            color: DARK_SURFACES.canvas,
            fontSize: { xs: 36, md: 58 },
            fontWeight: 950,
            lineHeight: 1,
            opacity: 0.95,
            animation: AUTH_KNIGHT_ANIMATION,
            "@keyframes authKnightMove": AUTH_KNIGHT_FRAMES,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              transform: authKnightTransform(AUTH_KNIGHT_PATH[2]),
            },
          }}
        >
          ♞
        </Box>
      </Box>
      <Box
        sx={{
          alignSelf: "end",
          maxWidth: 680,
          position: "relative",
          "@keyframes authCopyReveal": { from: { transform: "translateY(112%)" }, to: { transform: "translateY(0)" } },
        }}
      >
        <Typography
          component="p"
          sx={{
            color: ALERT_RED.main,
            fontSize: { xs: 46, sm: 72, lg: 108 },
            fontWeight: 950,
            letterSpacing: "-.1em",
            lineHeight: 0.78,
          }}
        >
          {["THINK.", "TEST.", "VERIFY."].map((line, index) => (
            <Box component="span" key={line} sx={{ display: "block", overflow: "hidden" }}>
              <Box
                component="span"
                data-auth-headline-reveal={line}
                sx={{
                  display: "block",
                  transform: "translateY(112%)",
                  animation: `authCopyReveal 680ms cubic-bezier(.22,1,.36,1) ${280 + index * 150}ms forwards`,
                  "@media (prefers-reduced-motion: reduce)": { animation: "none", transform: "none" },
                }}
              >
                {line}
              </Box>
            </Box>
          ))}
        </Typography>
        <Box sx={{ mt: 3, maxWidth: 440, overflow: "hidden" }}>
          <Typography
            data-auth-detail-reveal
            sx={{
              fontFamily: "monospace",
              fontSize: { xs: 10, md: 12 },
              lineHeight: 1.6,
              transform: "translateY(112%)",
              animation: "authCopyReveal 680ms cubic-bezier(.22,1,.36,1) 980ms forwards",
              "@media (prefers-reduced-motion: reduce)": { animation: "none", transform: "none" },
            }}
          >
            Finished games become private evidence. Nikola explains what the position proves, never what it cannot know.
          </Typography>
        </Box>
      </Box>
    </Box>
    <Box
      component="section"
      sx={{
        alignContent: "safe center",
        bgcolor: OFF_WHITE.main,
        color: DARK_SURFACES.canvas,
        display: "grid",
        minHeight: { md: "100dvh" },
        px: { xs: 2.5, sm: 6, lg: 9 },
        py: { xs: 5, md: 8 },
        pb: { xs: "max(40px, env(safe-area-inset-bottom))" },
      }}
    >
      <Box sx={{ mx: "auto", width: "min(100%, 480px)" }}>
        <Typography
          sx={{
            color: ALERT_RED.dark,
            fontFamily: "monospace",
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: ".14em",
            mb: 2,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 38, sm: 52 },
            fontWeight: 950,
            letterSpacing: "-.075em",
            lineHeight: 0.9,
            mb: detail ? 2 : 4,
          }}
        >
          {title}
        </Typography>
        {detail && (
          <Typography
            sx={{
              ...authMutedTextSx,
              borderLeft: `3px solid ${ALERT_RED.main}`,
              fontSize: 13,
              lineHeight: 1.6,
              mb: 4,
              pl: 2,
            }}
          >
            {detail}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  </Box>
);
